import { graphql } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React, { TouchEvent, useEffect, useState } from 'react';

import { ReactComponent as ChevronLeftSmall } from '/src/images/icons/chevron-left-small.svg';
import { ReactComponent as ChevronRightSmall } from '/src/images/icons/chevron-right-small.svg';
import { ReactComponent as Dot } from '/src/images/icons/dot.svg';
import { ReactComponent as IconPlaceholder } from '/src/images/icons/icon-placeholder.svg';

import { generateCorrectSlug } from '../../../helpers/generate-correct-slug';
import { IMeetingPlaceLinkToAnyPage } from '../../../queries/fragments/meetingPlaceLinkToAnyPage';
import {
  IImageWrapperFile,
  IImageWrapperSvg,
} from '../../../queries/fragments/svg';
import ImageWrapper from '../../image-wrapper/Image-wrapper';
import LinkButton from '../../ui/link-button/link-button';
import LinkWrapper from '../../ui/link-wrapper/link-wrapper';
import * as styles from './main-banner-list-block.module.scss';

interface MainBannerListItem {
  id: string;
  text: string;
  title: string;
  overlay: boolean;
  mediaField: {
    jsonBynderAsset: {
      originalUrl: string;
      thumbnail: string;
    };
  };
  readMoreButtonTitle: string;
  link?: IMeetingPlaceLinkToAnyPage;
  externalLink?: string;
}

export interface MainBannerListBlockProps {
  mainBannerItems: Array<MainBannerListItem>;
  meetingPlaceName?: string;
  logo?: {
    gatsbyImageData: IGatsbyImageData;
    svg: IImageWrapperSvg;
    file: IImageWrapperFile;
  };
  node_locale: string;
}

const MainBannerListBlock = ({
  mainBannerItems,
  logo,
  meetingPlaceName,
  node_locale,
}: MainBannerListBlockProps) => {
  const [currentBanner, setCurrentBanner] = useState<number>(0);
  const numberOfBanners = mainBannerItems?.length - 1;
  const timeoutRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  // autoplay
  useEffect(() => {
    resetTimeout();
    timeoutRef.current = setInterval(nextBanner, 5000);
    () => resetTimeout();
  }, [currentBanner]);

  const nextBanner = (): void => {
    if (currentBanner < numberOfBanners) {
      setCurrentBanner((prevBanner) => prevBanner + 1);
    } else if (currentBanner === numberOfBanners) {
      setCurrentBanner(0);
    }
  };

  const previousBanner = (): void => {
    if (currentBanner > 0) {
      setCurrentBanner((prevBanner) => prevBanner - 1);
    } else if (currentBanner === 0) {
      setCurrentBanner(numberOfBanners);
    }
  };

  const [touchStartPositionX, setTouchStartPositionX] = useState<number>(0);
  const [touchStartPositionY, setTouchStartPositionY] = useState<number>(0);
  const [touchDistanceX, setTouchDistanceX] = useState<number>(0);
  const [touchDistanceY, setTouchDistanceY] = useState<number>(0);
  const [touchStartTime, setTouchStartTime] = useState<number>(0);

  const touchMillisecondTreshold = 50; // Touch length in ms require to trigger event
  const touchHorizontalRestraint = 50; // Number of pixels in X axis to trigger a swipe

  const handleTouchStart = (touchEvent: TouchEvent): void => {
    const { pageX, pageY } = touchEvent.changedTouches[0];

    setTouchStartTime(new Date().getTime());
    setTouchStartPositionX(pageX);
    setTouchStartPositionY(pageY);
  };

  const handleTouchMove = (touchEvent: TouchEvent): void => {
    const { pageX, pageY } = touchEvent.changedTouches[0];
    const distanceX = pageX - touchStartPositionX;
    const distanceY = pageY - touchStartPositionY;
    setTouchDistanceX(distanceX);
    setTouchDistanceY(distanceY);
  };

  const handleTouchEnd = () => {
    const ellapsedTime = new Date().getTime() - touchStartTime;
    if (ellapsedTime > touchMillisecondTreshold) {
      if (
        Math.abs(touchDistanceX) >= touchHorizontalRestraint &&
        Math.abs(touchDistanceX) > Math.abs(touchDistanceY)
      ) {
        const direction = touchDistanceX < 0 ? 'forward' : 'backwards';
        setTouchDistanceX(0);
        setTouchDistanceY(0);
        switch (direction) {
          case 'forward':
            nextBanner();
            break;
          case 'backwards':
            previousBanner();
        }
      }
    }
  };

  const SliderNavigation = () => {
    return (
      <ul className="absolute bottom-16 left-0 right-0 z-10 mb-28 md:mb-12 sm:mb-10 flex w-full justify-center lg:mx-7 lg:mb-0 lg:justify-start lg:pl-4">
        <li>
          <button
            type="button"
            title="Previous"
            onClick={previousBanner}
            className={
              'mr-6 hidden h-8 w-8 rounded-full bg-brand-white lg:inline'
            }
          >
            <ChevronLeftSmall className="inline-block" />
          </button>
        </li>
        {mainBannerItems?.map((_, index) => {
          return (
            <li
              key={`slider-navigation-indicator-${index}`}
              className="mx-1.5 flex items-center"
              onClick={() => setCurrentBanner(index)}
            >
              {index === currentBanner ? (
                <IconPlaceholder className="brightness-0 invert" />
              ) : (
                <Dot className="brightness-0 invert" />
              )}
            </li>
          );
        })}
        <li>
          <button
            title="Next"
            type="button"
            onClick={nextBanner}
            className={
              'ml-6 hidden h-8 w-8 rounded-full bg-brand-white lg:inline'
            }
          >
            <ChevronRightSmall className="inline-block" />
          </button>
        </li>
      </ul>
    );
  };

  const hiddenSlide = 'opacity-0 pointer-events-none';
  const displayedSlide = 'opacity-100 pointer-events-auto';
  const aspectRatio =
    logo?.gatsbyImageData?.width && logo?.gatsbyImageData?.height
      ? logo.gatsbyImageData.width / logo.gatsbyImageData.height
      : undefined;

  return (
    <section className="relative top-0 z-10 bg-brand-white lg:sticky lg:mx-0 lg:mt-0 lg:pt-6">
      {logo && (
        <LinkWrapper
          data={`/${node_locale}`}
          className="absolute left-8 top-12 lg:top-12 sm:top-6 z-10 block lg:left-12"
        >
          <ImageWrapper
            className={aspectRatio == 1 ? 'max-h-15 w-36' : 'max-h-10 w-36'}
            alt="Logotype"
            file={logo.file}
            gatsbyImageData={logo.gatsbyImageData}
            svg={logo.svg}
            imgClassName={
              aspectRatio == 1 ? 'max-h-15 !w-auto' : 'max-h-10 !w-auto'
            }
          />
        </LinkWrapper>
      )}

      <div className="relative flex h-screen overflow-hidden  lg:h-[calc(100vh-3rem)]">
        {mainBannerItems?.map((banner, index) => {
          return (
            <div
              onTouchStart={(event: TouchEvent) => handleTouchStart(event)}
              onTouchMove={(event: TouchEvent) => handleTouchMove(event)}
              onTouchEnd={() => handleTouchEnd()}
              key={`${banner.id}-${index}`}
              className={`group absolute inset-0 transition-all delay-200 duration-500 lg:rounded-xxl ${
                currentBanner === index ? displayedSlide : hiddenSlide
              }`}
              id={`banner-${index}`}
            >
              {banner.mediaField &&
                banner.mediaField?.jsonBynderAsset &&
                banner.mediaField?.jsonBynderAsset?.originalUrl && (
                  <div className="!absolute inset-0 h-screen lg:h-auto">
                    <img
                      src={
                        banner.mediaField.jsonBynderAsset.originalUrl +
                        '?fm=avif'
                      }
                      alt={''}
                      className="absolute inset-0 h-full w-full object-cover lg:rounded-xxl"
                    />
                  </div>
                )}
              <div
                className={`${
                  banner.overlay ? styles.gradient : ''
                } absolute inset-0 lg:rounded-b-xxl`}
              ></div>
              <div
                className={`absolute bottom-57 md:bottom-40 sm:bottom-20 px-7 lg:bottom-40 lg:px-12 ${
                  currentBanner === index ? '' : 'animate-fadeOutDown'
                }`}
              >
                <h1 className="mb-3 font-title text-headingxl text-brand-yellow-1 lg:mb-2 ">
                  {banner.title}
                </h1>
                <p className="mb-5 text-bodym text-brand-white lg:mb-8 lg:text-bodyl">
                  {banner.text}
                </p>
                {(banner.link?.slug || banner.externalLink) &&
                  banner.readMoreButtonTitle && (
                    <LinkButton
                      to={
                        banner.link
                          ? generateCorrectSlug(banner.link)
                          : banner.link && banner.externalLink
                            ? generateCorrectSlug(banner.link)
                            : (banner.externalLink as string)
                      }
                      variant="primary"
                      isExternalLink={
                        banner.link
                          ? false
                          : banner.link && banner.externalLink
                            ? false
                            : true
                      }
                      openInNewTab={
                        banner.link
                          ? false
                          : banner.link && banner.externalLink
                            ? false
                            : true
                      }
                      size="regular"
                      includeIosDoubleTapFix
                    >
                      {banner.readMoreButtonTitle}
                    </LinkButton>
                  )}
              </div>
            </div>
          );
        })}
        <SliderNavigation />
      </div>
    </section>
  );
};

export default MainBannerListBlock;

export const query = graphql`
  fragment MainBannerBlock on ContentfulBlockMainBanner {
    blocktype: __typename
    text
    title
    overlay
    mediaField {
      jsonBynderAsset {
        originalUrl
        thumbnail
      }
    }
    readMoreButtonTitle
    link {
      ...MeetingPlaceLinkToAnyPage
    }
    externalLink
  }
`;
